import { Form, Formik, FormikProps, FormikErrors } from 'formik';
import { FormInputWithLabel } from '../../../_core/form/input/form-labelled-input.component';
import { FormButton } from '../../../_core/form/button/form-button.component';
import { IRIFormProps } from '../../../_core/form/form.types';
import { FormActionsRow } from '../../../_core/form/input/form-actions-row';
import { getUrlValidationError } from '../../../../lib/url-validation';
import { useUploadAsset } from '../../assets/asset.service';
import { useState } from 'react';
import { IAsset, ROLE } from '../../../../lib/types';

export interface IUpdateCompanyFormValues {
  name?: string;
  defaultPostSubmissionRedirectUrl?: string;
  logoAssetId?: string;
}

interface IUpdateCompanyFormProps extends IRIFormProps<IUpdateCompanyFormValues> {
  name: string;
  defaultPostSubmissionRedirectUrl?: string;
  logoUrl?: string;
  logoAssetId?: IAsset;
  memberRole: ROLE;
}

export const UpdateCompanyForm = ({ name, defaultPostSubmissionRedirectUrl, logoUrl, logoAssetId, onSubmit, memberRole }: IUpdateCompanyFormProps) => {
  const { uploadAsset } = useUploadAsset();
  const [uploadedLogoId, setUploadedLogoId] = useState<string | undefined>(logoAssetId?._id);
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | undefined>(logoUrl);
  const [uploading, setUploading] = useState(false);

  const validateForm = (values: IUpdateCompanyFormValues) => {
    const errors: FormikErrors<IUpdateCompanyFormValues> = {};

    if (values.defaultPostSubmissionRedirectUrl) {
      const urlError = getUrlValidationError(values.defaultPostSubmissionRedirectUrl);
      if (urlError) {
        errors.defaultPostSubmissionRedirectUrl = urlError;
      }
    }

    return errors;
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, SVG, or WebP image file.');
      return;
    }

    // Validate file size (10 MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 10 MB.');
      return;
    }

    setUploading(true);
    try {
      // Upload with isPublic: true so the logo can be accessed in emails
      const response = await uploadAsset(file, { isPublic: true });
      if (response?.data?.asset) {
        setUploadedLogoId(response.data.asset._id);
        setCurrentLogoUrl(response.data.asset.url);
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setUploadedLogoId('');
    setCurrentLogoUrl(undefined);
  };

  const isCompanyOwner = memberRole === ROLE.COMPANY_OWNER;

  return (
    <Formik
      initialValues={{
        name,
        defaultPostSubmissionRedirectUrl: defaultPostSubmissionRedirectUrl || '',
        logoAssetId: uploadedLogoId
      } as IUpdateCompanyFormValues}
      onSubmit={(values) =>
        onSubmit({
          ...values,
          name: isCompanyOwner ? values.name?.trim() : undefined,
          defaultPostSubmissionRedirectUrl: values.defaultPostSubmissionRedirectUrl?.trim() || '',
          logoAssetId: uploadedLogoId
        })
      }
      validate={validateForm}
    >
      {(props: FormikProps<IUpdateCompanyFormValues>) => {
        return (
        <Form action="#" method="POST" className="sm:max-w-xl mx-auto space-y-4 mt-10">
          {isCompanyOwner && (<FormInputWithLabel
              name="name"
              label="Name"
              placeholder="Edit organization name"
            />
          )}

          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Organization Logo</h3>

            {currentLogoUrl && (
              <div className="mb-4">
                <img
                  src={currentLogoUrl}
                  alt="Organization logo"
                  className="max-w-xs max-h-32 object-contain border rounded p-2"
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                  Remove Logo
                </button>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {currentLogoUrl ? 'Replace Logo' : 'Upload Logo'}
              </label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  disabled:opacity-50"
              />
              {uploading && <p className="mt-2 text-sm text-gray-600">Uploading...</p>}
              <p className="mt-2 text-sm text-gray-600">
                Supported formats: PNG, JPG, SVG, WebP. Max size: 10 MB.
              </p>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Default Post-Submission Settings</h3>

            <FormInputWithLabel
              name="defaultPostSubmissionRedirectUrl"
              label="Default Post-Submission Redirect URL (Optional)"
              placeholder="https://example.com/thank-you"
            />

            <div className="text-sm text-gray-600 mt-2">
              <p>This is the default redirect URL for all request types in this organization. It can be overridden per request type.</p>
            </div>
          </div>

          <FormActionsRow>
            <FormButton size="medium" loading={props.isSubmitting || uploading} text="Update" />
          </FormActionsRow>
        </Form>
        );
      }}
    </Formik>
  );
};
